import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Search, FileText, Clock, CheckCircle2, XCircle, Edit3, ChevronRight,
  Eye, X, Plus,
} from 'lucide-react';

const statoConfig = {
  inviata: { label: 'Inviata', icon: Clock, bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  'in-bozza': { label: 'In Bozza', icon: Edit3, bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  rifiutata: { label: 'Rifiutata', icon: XCircle, bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  accettata: { label: 'Accettata', icon: CheckCircle2, bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
};

const mockRichieste = [
  { id: 'req-1', data: '17/02/2026', paziente: 'Colombo Maria', tipologia: 'ADI Complessa (CIA-4)', stato: 'inviata', motivazione: 'Paziente con demenza senile avanzata, necessita assistenza domiciliare integrata per supporto nelle attività quotidiane e monitoraggio parametri vitali. Caregiver principale in stato di burnout, necessario intervento educativo.', priorita: '10 giorni (B)' },
  { id: 'req-2', data: '10/02/2026', paziente: 'Neri Giuseppe', tipologia: 'ADI Post-Chirurgica (CIA-2)', stato: 'accettata', motivazione: 'Frattura femore sx trattata chirurgicamente. Dimissione protetta da Policlinico Gemelli. Richiesta fisioterapia domiciliare intensiva e medicazione ferita chirurgica.', priorita: '72 ore (U)' },
  { id: 'req-3', data: '05/02/2026', paziente: 'Romano Pietro', tipologia: 'ADI Infermieristica (CIA-2)', stato: 'accettata', motivazione: 'Piede diabetico con ulcera di grado 2 al tallone destro. Necessarie medicazioni avanzate a domicilio 3 volte/settimana e monitoraggio glicemico.', priorita: '10 giorni (B)' },
  { id: 'req-4', data: '20/01/2026', paziente: 'Mancini Bruno', tipologia: 'ADI Infermieristica (CIA-1)', stato: 'accettata', motivazione: 'Paziente in terapia anticoagulante orale (TAO). Richiesta monitoraggio INR domiciliare settimanale e somministrazione farmaci per via iniettiva.', priorita: '30 giorni (D)' },
  { id: 'req-5', data: '18/02/2026', paziente: 'Bianchi Mario', tipologia: 'ADI Dimissione Protetta (CIA-3)', stato: 'in-bozza', motivazione: '', priorita: '' },
  { id: 'req-6', data: '12/01/2026', paziente: 'De Luca Teresa', tipologia: 'ADI Infermieristica (CIA-1)', stato: 'rifiutata', motivazione: 'Paziente autosufficiente con necessità di monitoraggio pressorio a domicilio. Proposta assistenza infermieristica territoriale settimanale.', priorita: '120 giorni (P)' },
];

export default function MMGRequestsHistory() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [viewRequest, setViewRequest] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return mockRichieste;
    const q = search.toLowerCase();
    return mockRichieste.filter(r => r.paziente.toLowerCase().includes(q) || r.tipologia.toLowerCase().includes(q));
  }, [search]);

  return (
    <AppLayout role="mmg">
      <div className="space-y-6" data-testid="mmg-requests-history">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground" data-testid="page-title">Le Mie Richieste</h1>
            <p className="text-sm text-muted-foreground mt-1">Storico delle richieste ADI inviate</p>
          </div>
          <Button
            className="gap-2 rounded-xl h-11 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm"
            onClick={() => navigate('/mmg/nuova-richiesta')}
            data-testid="cta-nuova-richiesta"
          >
            <Plus className="w-4 h-4" />Crea Nuova Richiesta ADI
          </Button>
        </div>

        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border/20">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  className="w-full h-10 pl-9 pr-3 rounded-xl border border-border/40 bg-muted/20 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Cerca per paziente o tipologia..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-richieste"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="richieste-table">
                <thead>
                  <tr className="border-b border-border/20 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <th className="text-left px-5 py-3">Data</th>
                    <th className="text-left px-5 py-3">Paziente</th>
                    <th className="text-left px-5 py-3">Tipologia</th>
                    <th className="text-left px-5 py-3">Stato</th>
                    <th className="text-right px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/10">
                  {filtered.map((r) => {
                    const st = statoConfig[r.stato] || statoConfig.inviata;
                    const StIcon = st.icon;
                    const isBozza = r.stato === 'in-bozza';
                    return (
                      <tr key={r.id} className="hover:bg-muted/10 transition-colors" data-testid={`richiesta-row-${r.id}`}>
                        <td className="px-5 py-3.5 text-xs text-muted-foreground font-medium">{r.data}</td>
                        <td className="px-5 py-3.5 text-sm font-semibold text-foreground">{r.paziente}</td>
                        <td className="px-5 py-3.5 text-xs text-muted-foreground">{r.tipologia}</td>
                        <td className="px-5 py-3.5">
                          <Badge className={`text-[10px] border gap-1 ${st.bg} ${st.text} ${st.border}`}>
                            <StIcon className="w-3 h-3" />{st.label}
                          </Badge>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          {isBozza ? (
                            <button
                              onClick={() => navigate('/mmg/nuova-richiesta')}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
                              data-testid={`btn-compila-${r.id}`}
                            >
                              <Edit3 className="w-3 h-3" /> Compila
                            </button>
                          ) : (
                            <button
                              onClick={() => setViewRequest(r)}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                              data-testid={`btn-visualizza-${r.id}`}
                            >
                              <Eye className="w-3 h-3" /> Visualizza
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-sm text-muted-foreground">Nessuna richiesta trovata.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ══ MODALE READ-ONLY ══ */}
      {viewRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="modal-dettaglio-richiesta">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewRequest(null)} />
          <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-sm font-bold text-slate-800 dark:text-white">Dettaglio Richiesta ADI</h2>
              </div>
              <button onClick={() => setViewRequest(null)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="modal-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex flex-col gap-4">
              {/* Stato Badge */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500 dark:text-slate-400">ID: {viewRequest.id}</span>
                {(() => {
                  const st = statoConfig[viewRequest.stato] || statoConfig.inviata;
                  const StIcon = st.icon;
                  return (
                    <Badge className={`text-[10px] border gap-1 ${st.bg} ${st.text} ${st.border}`}>
                      <StIcon className="w-3 h-3" />{st.label}
                    </Badge>
                  );
                })()}
              </div>

              {/* Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Paziente</label>
                  <div className="px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm font-medium text-slate-800 dark:text-white">{viewRequest.paziente}</div>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Data Richiesta</label>
                  <div className="px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm text-slate-800 dark:text-white">{viewRequest.data}</div>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Tipologia ADI</label>
                <div className="px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm text-slate-800 dark:text-white">{viewRequest.tipologia}</div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Priorità</label>
                <div className="px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm text-slate-800 dark:text-white">{viewRequest.priorita || '—'}</div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Motivazione Clinica</label>
                <div className="px-3 py-3 rounded-lg bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 leading-relaxed min-h-[80px]">
                  {viewRequest.motivazione || <span className="text-slate-400 italic">Nessuna motivazione inserita.</span>}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-800">
              <button onClick={() => setViewRequest(null)} className="px-5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid="modal-chiudi-btn">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
